// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartDetails model
 */

import { action, observable, untracked, computed } from 'mobx';
import {
  getOr,
  flatMap,
  filter,
  reduce,
  get,
  flow,
  compact,
  concat,
  uniq,
  mapValues,
  find,
  map,
  isUndefined,
  omit,
  floor,
  isFunction,
  invoke,
  invokeArgs,
  trim,
  isNaN,
  isNil,
  sortBy,
  isArray,
  forEach,
  isEmpty,
  max,
  head
} from 'lodash/fp';

import ReportPartElement from './Izenda.ReportPartElement';
import ReportPartQueryData from './Izenda.ReportPartQueryData';

import { DIRECTION_STATE, BODY_CELL_WIDTH, BODY_CELL_HEIGHT } from 'logic/data/Izenda.ReportBodyConstants';
import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import { MAX_LENGTH_PREVIEW_TITLE } from 'logic/data/Izenda.ReportPartGridConstants';
import calculatedFieldConstant from 'logic/data/Izenda.CalculatedFieldConstants';
import AppGlobal from 'Izenda.AppGlobal';
import tracker from 'share/tracking/Izenda.Tracker';
import reportPartConfigFactories from 'share/factories/Izenda.ReportPartConfigurationFactories';
import { ContainerName } from 'logic/data/Izenda.ReportPartChartConstants';
import IzendaUIConstants from '../data/Izenda.UIConstants';

@tracker
class ReportPartDetails {
  @observable accessPriority;
  @observable isActive = false;
  @observable reportPartContent = null;
  @observable headerTitle = '';
  @observable
  previewTitle = {
    text: '',
    properties: {},
    settings: {
      font: {
        family:IzendaUIConstants.fontFamily.proxima,
        size: 14,
        bold: true,
        italic: false,
        underline: false,
        color: '',
        highlightColor: ''
      },
      alignment: {
        alignment: ''
      }
    },
    elements: []
  };
  @observable
  description = {
    text: '',
    properties: {},
    settings: {
      font: {
        family:IzendaUIConstants.fontFamily.proxima,
        size: 14,
        bold: false,
        italic: false,
        underline: false,
        color: '',
        highlightColor: ''
      },
      alignment: {
        alignment: ''
      }
    },
    elements: []
  };
  @observable reportId = null;
  @observable id = '0';
  @observable positionX = 0;
  @observable positionY = 0;
  @observable width = 1;
  @observable height = 1;
  @observable state = 0;
  @observable modified = null;
  @observable numberOfRecord = null;
  @observable isBackSide = false;
  @observable allowToSave = false;
  @observable queryData = [];
  @observable configField = {};
  @observable isLoadingData = true;
  @observable newestField = null;
  @observable onDrilldown = null;
  @observable stateReview = AppGlobal.canShowPreview() ? DIRECTION_STATE.NORMAL : DIRECTION_STATE.LEFT;
  reportInfo = [];

  // check if report part is ready to get data
  @computed
  get isBeingBuild() {
    return this.reportPartContent && this.reportPartContent.isBeingBuild;
  }
  @computed
  get containerCollection() {
    return this.reportPartContent && this.reportPartContent.containerCollection
      ? this.reportPartContent.containerCollection
      : [];
  }
  @computed
  get fieldCollection() {
    return flatMap(item => (item.elements ? item.elements.slice() : []), this.containerCollection);
  }
  @computed
  get configurableFieldCollection() {
    return flow(
      filter(item => item.name !== ContainerName.DYNAMIC_THRESHOLD),
      flatMap(item => (item.elements ? item.elements.slice() : []))
    )(this.containerCollection);
  }
  @computed
  get visibleFieldCollection() {
    return filter(item => !!item.field.visible, this.fieldCollection);
  }

  @computed
  get autoSize() {
    return getOr(false, 'autoSize', this.reportPartContent);
  }

  @computed
  get embededInfo() {
    const result = reduce(
      (acc, item) => {
        acc.reportIds.push(get('embededInfo.reportId', item));
        acc.reportPartIds = acc.reportPartIds.concat(getOr([], 'embededInfo.reportPartIds', item));
        return acc;
      },
      {
        reportIds: [],
        reportPartIds: []
      },
      this.fieldCollection
    );
    const getUniq = flow(
      compact,
      uniq
    );

    return mapValues(getUniq, result);
  }
  @computed
  get type() {
    return this.reportPartContent && this.reportPartContent.type;
  }
  @computed
  get isNew() {
    return !String.isGuidFormat(this.id);
  }
  @computed
  get isDelete() {
    return this.state === constants.OBJECT_STATE.DELETE;
  }
  @computed
  get previewData() {
    return this.queryData.length === 1 ? this.queryData[0] : this.queryData;
  }
  @computed
  get previewDataFieldsMapping() {
    return this.previewData.fieldsMapping || [];
  }
  @computed
  get previewDataRecords() {
    return this.previewData.records || [];
  }
  @computed
  get cities() {
    return this.previewData.cities || [];
  }
  @computed
  get postalCodes() {
    return this.previewData.postalCodes || [];
  }
  @computed
  get countryCodes() {
    return this.previewData.countryCodes || [];
  }
  @computed
  get stateCodes() {
    return this.previewData.stateCodes || [];
  }
  @computed
  get countyCodes() {
    return this.previewData.countyCodes || [];
  }
  @computed
  get addressCodes() {
    return this.previewData.addressCodes || [];
  }
  @computed
  get groupTotalValues() {
    return this.previewData.groupTotalValues || {};
  }
  @computed
  get existInvisibleField() {
    const invisibleField = find(item => !item.properties.fieldItemVisible, this.fieldCollection);
    return !!invisibleField;
  }
  @computed
  get aggregatedFieldCollection() {
    return map(field => {
      if (!this.isDelete && get('isAggregatedField', field)) {
        return {
          dataType: field.fieldDataType,
          sourceFieldName: field.field.fieldNameAlias,
          reportFieldAlias: field.field.fieldNameAlias,
          querySourceFieldId: field.fieldId,
          sourceDataObjectName: 'Aggregated Field',
          querySourceType: 'Aggregated Field',
          sourceFieldVisible: true,
          sourceFieldFilterable: true,
          sortType: 'Unsorted',
          reportPartTitle: this.headerTitle
        };
      }
    }, this.fieldCollection);
  }
  @computed
  get shouldUpdatePreview() {
    return AppGlobal.canShowPreview() && this.stateReview !== DIRECTION_STATE.LEFT;
  }

  /**
   * Constructor
   * @param {Object} data
   * @param {boolean} isCopy
   * @param {Object} accessPriority
   * @param {boolean} viewMode
   * @param {boolean} editMode
   */
  constructor(data: any, isCopy: boolean, accessPriority: any, viewMode: Boolean, editMode: boolean) {
    this.accessPriority = accessPriority;
    this.embedReportDef = {
      reports: {},
      reportParts: {}
    };

    // element info store the container and position of report part element after save.
    // The element may change container and position when edit so we need to cache this info for sub report field mapping
    this.elementInfo = [];

    this.colorPalettes = [];
    this.validateDetail = null;
    data && this.readData(data, isCopy, accessPriority, viewMode, editMode);
    this.cleanDirty();
  }

  /**
   * read data from API or from ReportBody addReportPart function
   * @param {Object} data JSON object preresent data
   * @param {boolean} isCopy
   * @param {Object} accessPriority
   * @param {boolean} viewMode
   * @param {boolean} editMode
   */
  @action
  readData(data: any, isCopy: boolean, accessPriority: any, viewMode: boolean, editMode: boolean): void {
    const type = data.reportPartContent && data.reportPartContent.type;
    this.id = data.id;
    this.isActive = data.isActive;
    this.headerTitle = data.title || '';
    this.reportId = data.reportId;
    this.positionX = data.positionX;
    this.positionY = data.positionY;
    this.width = data.width;
    this.height = data.height;
    this.state = getOr(constants.OBJECT_STATE.INSERT, 'state', data);
    this.numberOfRecord = data.numberOfRecord;
    if (!isUndefined(accessPriority)) {
      this.accessPriority = accessPriority;
    }

    this.setReportPartType(type);
    this.reportPartContent.readData && this.reportPartContent.readData(data.reportPartContent, isCopy, viewMode, editMode);
    this.elementInfo = this.buildElementInfo();

    if (data.reportPartContent) {
      data.reportPartContent.title && (this.previewTitle = data.reportPartContent.title);
      data.reportPartContent.description && (this.description = data.reportPartContent.description);
    }

    this.newestField = null;
    this.allowToSave = data.allowToSave;
  }

  /**
   * build report part object for save
   * @returns {Object} save object
   */
  buildDataForSave(): any {
    const saveObject = omit(
      [
        'id',
        'headerTitle',
        'previewTitle',
        'description',
        'previewData',
        'embedReportDef',
        'queryData',
        'positionX',
        'positionY',
        'elementInfo',
        'newestField',
        'isLoadingData',
        'onDrilldown',
        'isBackSide'
      ],
      this
    );

    // TODO: ask Tung Thach about ReportBodyContent line 94-101
    // HACK: ceil value of positionY and positionX when save data because backend only support interger
    saveObject.positionY = floor(this.positionY);
    saveObject.positionX = floor(this.positionX);

    saveObject.title = this.headerTitle;
    if (!saveObject.reportPartContent) {
      saveObject.reportPartContent = {};
    }
    if (isFunction(saveObject.reportPartContent.buildDataForSave)) {
      saveObject.reportPartContent = saveObject.reportPartContent.buildDataForSave();
    }
    saveObject.reportPartContent.title = this.previewTitle;
    saveObject.reportPartContent.description = this.description;

    this.checkDirty() && saveObject.state === constants.OBJECT_STATE.NONE && (saveObject.state = constants.OBJECT_STATE.UPDATE);
    !this.isNew && (saveObject.id = this.id);
    return saveObject;
  }

  /**
   * Set report part position
   * @param {Object} position object
   * @param {boolean} whether mark dirty or not
   */
  @action
  setPosition(position: any, manual: boolean): void {
    this.positionX = position.positionX;
    this.positionY = position.positionY;
    this.width = position.width;
    this.height = position.height;

    manual && this.markDirty();
  }

  /**
   * Set report part type
   * @param {string} value Type of report part
   */
  @action
  setReportPartType(reportPartType: string): void {
    const cfg = reportPartType && reportPartConfigFactories.getConfiguration(reportPartType);

    const hasConfig = cfg && cfg.model;

    let containersHistory = [];
    let containersHistoryMetric = [];
    if (this.reportPartContent) {
      containersHistory = concat(this.reportPartContent.containersHistory, []);
      containersHistoryMetric = concat(this.reportPartContent.containersHistoryMetric, []);
    }

    this.reportPartContent = hasConfig ? new cfg.model(null, containersHistory, containersHistoryMetric) : { type: reportPartType };

    // If report part type is auto-sized, set fix width & height as it doesn't
    // have any meanings. Its size is dynamically determined when rendering, not
    // at design time.
    if (this.isNew && this.reportPartContent.autoSize) {
      this.width = 0;
      this.height = 0;
    }

    if (this.isNew) {
      this.markDirty();
    }
  }

  /**
   * clear all field in container
   * sample use case : clear separators container when uncheck
   * @param {Object} container
   */
  @action
  clearContainer(container: any): void {
    container.elements = observable([]);
  }

  /**
   * check drity status of this report part
   * @returns {boolean} dirty status
   */
  checkDirty(): boolean {
    const reportPartContent = this.reportPartContent;

    return this.isDirty || (reportPartContent && reportPartContent.checkDirty && reportPartContent.checkDirty());
  }

  /**
   * Clean dirty status.
   */
  @action
  cleanDirty(): void {
    invoke('cleanDirty', this.reportPartContent);

    this.resetDirty();
  }

  /**
   * insert new field as element into report part container
   * @param {Object} container   container will be insertFieldToContainer
   * @param {Object} field       field that element preresent
   * @param {Object} querySource query source contain field
   * @param {number} position
   * @param {Object} functionData function.
   * @returns {Object}
   */
  @action
  insertFieldToContainer(
    container: any,
    field: any,
    querySource: any,
    position: number,
    functionData: any,
    preloadPromises: Object,
    needReloadData: Boolean = true
  ): any {
    const fieldName = field.alias || field.name;
    const nextPosition =
      position ||
      (flow(
        map('position'),
        max
      )(container.elements) || 0) + 1;

    const reportPartType = this.reportPartContent.type;
    const reportPartStyle = this.reportPartContent.reportPartStyle;
    const containerName = container.name;
    const defaultSort = reportPartUtils.getDefaultSort(reportPartType, reportPartStyle, containerName);
    const newElement = new ReportPartElement(field, querySource, fieldName, nextPosition, defaultSort);

    functionData && newElement.properties.setFunction(functionData);

    return this.commonInsertField(fieldName, newElement, container, nextPosition, !!functionData, preloadPromises, needReloadData);
  }

  /**
   * Common Insert Field
   * @param {string} fieldName
   * @param {Object} newElement
   * @param {Object} container
   * @param {number} nextPosition
   * @param {boolean} notUpdateSelectedField
   * @param {Object} preloadPromises [optional] promises which preload functions and formats.
   * @returns {Object}
   */
  @action
  commonInsertField(
    fieldName: string,
    newElement: any,
    container: any,
    nextPosition: number,
    notUpdateSelectedField: boolean,
    preloadPromises: Object,
    needReloadData: Boolean = true
  ): any {
    let addDefaultFunctionPromise = Promise.resolve();
    nextPosition =
      nextPosition ||
      (flow(
        map('position'),
        max
      )(container.elements) || 0) + 1;

    newElement.setUniqueName(fieldName, this);
    this.markDirty();

    // if no function has been set up: set up default function.
    if (!newElement.haveFunction && !newElement.field.isRunningField) {
      addDefaultFunctionPromise = reportPartUtils.setDefaultFunctionElement(
        this.reportPartContent,
        container,
        newElement,
        preloadPromises
      );
    }

    const insertElementToContainerElementsArray = action(() => {
      let needIncreasePosition = true;
      const uniqueNewName = reportPartUtils.getUniqueName(
        this.reportPartContent,
        newElement,
        newElement.fieldNameAlias
      );

      newElement.setName(uniqueNewName);

      if (container.elements.slice()[nextPosition - 1]) {
        container.elements.splice(nextPosition - 1, 0, newElement);
      } else {
        needIncreasePosition = false;
        if (nextPosition > container.elements.length) {
          container.elements.length = nextPosition;
        }
        container.elements[nextPosition - 1] = newElement;
      }

      // keep a reference to latest element which is added to element-list
      !notUpdateSelectedField && (this.newestField = newElement);

      // update position of each field
      if (needIncreasePosition) {
        for (let i = nextPosition; i < container.elements.length; i++) {
          container.elements[i].position++;
        }
      }
      // update isLoadingData to prevent render old data in report part
      needReloadData && (this.isLoadingData = true);

      return invokeArgs('afterInsertFieldToContainer', [container, newElement], this.reportPartContent) || null;
    });

    return addDefaultFunctionPromise.then(insertElementToContainerElementsArray).return(newElement);
  }

  /**
   * set report part name
   * @param {string} name new name
   */
  @action
  setReportPartName(name: string): void {
    this.headerTitle = trim(name);
    this.markDirty();
  }

  /**
   * set number of record
   * @param {string} numRecord input from ui
   */
  @action
  setNumberOfRecord(numRecord: string): void {
    const number = parseInt(numRecord, 10);
    if (isNaN(number)) {
      this.numberOfRecord = '';
    } else {
      this.numberOfRecord = number <= 0 ? 0 : number;
    }
    this.markDirty();
  }

  /**
   * remove elements from report part
   * @param {Object} container container need remove elements
   * @param {Object} deleteElement element need to delete
   */
  @action
  removeFieldFromContainer(container: any, deleteElement: any): void {
    const idx = container.elements.indexOf(deleteElement);

    // reset config field
    if (this.configField === deleteElement) {
      this.resetConfigField();
    }

    // remove from container
    idx > -1 && container.elements.splice(idx, 1);

    // update position of elements after it
    flow(
      filter(current => current.position > deleteElement.position),
      forEach(item => {
        item.position = item.position - 1;
      })
    )(container.elements);
    invoke('delete', deleteElement);

    this.reportPartContent.afterRemoveField && this.reportPartContent.afterRemoveField(container, deleteElement);

    this.markDirty();
  }

  /**
   * mark report part deleted
   */
  @action
  delete(): void {
    ensure(!this.isDelete, 'can\'t delete deleted object');
    !this.isNew && this.markDirty();
    this.state = constants.OBJECT_STATE.DELETE;

    // clear data refresh intervals (if any)
    this.clearRefreshData();
  }

  /**
   * Change position of field
   * @param {Object} field
   * @param {number} newPosition
   * @param {Object} container
   */
  @action
  changePositionField(field: any, newPosition: number, container: any): void {
    if (newPosition > field.position + 1) {
      for (let i = field.position; i < newPosition - 1; i++) {
        container.elements[i].position--;
      }
      field.position = newPosition - 1;
    } else if (newPosition < field.position) {
      for (let i = newPosition - 1; i < field.position - 1; i++) {
        container.elements[i].position++;
      }
      field.position = newPosition;
    }

    if (container) {
      container.elements = observable(sortBy('position', container.elements));
    }

    invokeArgs('changePositionField', arguments, this.reportPartContent);
    this.markDirty();
  }

  /**
   * Change container of a field
   * @param {Object} field
   * @param {Object} newPosition
   * @param {Object} sourceContainer
   * @param {Object} targetContainer
   * @returns {Object}
   */
  @action
  changeFieldContainer(field: any, newPosition: any, sourceContainer: any, targetContainer: any): any {
    const reportPartType = get('reportPartContent.type', this);
    const reportPartStyle = get('reportPartContent.reportPartStyle', this);
    const containerName = targetContainer.name;

    const defaultSort = reportPartUtils.getDefaultSort(reportPartType, reportPartStyle, containerName);
    // Clone new element
    const newElement = new ReportPartElement().readData(field);
    newElement.position = newPosition;
    newElement.resetProperties(defaultSort);

    // Remove field from source conatiner avoid wrong field name alias
    this.removeFieldFromContainer(sourceContainer, field);

    return this.commonInsertField(newElement.field.fieldName, newElement, targetContainer, newPosition);
  }

  /**
   * Check duplicate field name alias in report part.
   * @param {any} checkingField
   * @param {string} newName
   * @returns {boolean}
   */
  checkExistingFieldNameAlias(checkingField: any, newName: string): boolean {
    const isFieldNameAliasChanged = !String.checkDuplicate(newName, [checkingField.fieldNameAlias]);
    return isFieldNameAliasChanged && String.checkDuplicate(newName, map((item) => item.fieldNameAlias, this.fieldCollection));
  }

  /**
   * update title settings
   * @param {Object} titleSettings
   */
  @action
  updateTitleSettings(titleSettings: any): void {
    this.previewTitle.settings = this.getFontSettings(titleSettings);
    this.markDirty();
  }

  /**
   * update title text
   * @param {string} newPreviewTitle
   */
  @action
  updateReportPartPreviewTitle(newPreviewTitle: string): void {
    this.previewTitle.text =
      newPreviewTitle.length <= MAX_LENGTH_PREVIEW_TITLE ? newPreviewTitle : this.previewTitle.text;
    this.markDirty();
  }

  /**
   * update description settings
   * @param {Object} descriptionSettings
   */
  @action
  updateDescriptionSettings(descriptionSettings: any): void {
    this.description.settings = this.getFontSettings(descriptionSettings);
    this.markDirty();
  }

  /**
   * update description text
   * @param {string} descriptionText [new description text]
   */
  @action
  updateReportPartDescription(descriptionText: string): any {
    this.description.text = descriptionText;
    this.markDirty();
  }

  /**
   * convert to font setting for UI
   * @param {Object} settings object contain font settings
   * @returns {Object}         object contain convert font setting
   */
  getFontSettings(settings: any): any {
    const fontSettings = {
      font: {
        family: settings.fontFamily,
        size: settings.fontSize,
        bold: settings.fontBold,
        italic: settings.fontItalic,
        underline: settings.fontUnderline,
        color: settings.fontColor,
        highlightColor: settings.fontHighlightColor
      },
      alignment: {
        alignment: settings.alignment
      }
    };

    return fontSettings;
  }

  /**
   * update previewData that use for display preview report part
   * @param {Object} previewData preview data from api
   * @param {boolean} isLoadingData
   */
  @action
  setQueryData(previewData: any, isLoadingData: boolean): void {
    let data = isArray(previewData) ? previewData : [previewData];
    this.setLookupErrorMassage(previewData.messages);
    previewData.messages && (data = []);

    // NOTE: we cannot use replace() of ObservableArray here. The EVO cannot run that method.
    this.queryData = map(dataset => new ReportPartQueryData(dataset), data);
    this.isLoadingData = isLoadingData || false;

    // this.previewData = previewData;
  }

  /**
   * Get temporary state data
   * @returns {Object}
   */
  getTemporaryStateData(): any {
    return {
      drilldownInfo: get('reportPartContent.drilldownInfo', this),
      activePointOption: get('reportPartContent.activePointOption', this),
      selectedExpandedLevel: get('reportPartContent.selectedExpandedLevel', this),
      pointOptionsList: get('reportPartContent.pointOptionsList', this)
    };
  }

  /**
   * Restore temporary state data
   * @param {*} data 
   */
  restoreTemporaryStateData(data: any): void {
    if(isNil(data)) return;

    const reportPartContent = this.reportPartContent;
    if(isNil(reportPartContent)) return;

    reportPartContent.selectedExpandedLevel = data.selectedExpandedLevel;

    if(!isNil(data.activePointOption)){
      const reportPartState = getOr({}, 'properties.reportPartState', reportPartContent);
      const { field: { fieldId }, pointType } = reportPartState.activePointOption;
      if(fieldId != data.activePointOption.field.fieldId
        || pointType != data.activePointOption.pointType)
        reportPartState.activePointOption = data.activePointOption;
    }

    if(!isNil(data.drilldownInfo)){
      if(this.type === constants.REPORT_PART_TYPE.Map) {
        const drilldownInfo = reportPartContent.drilldownInfo;
        drilldownInfo.clear();
        forEach(item => drilldownInfo.push(item), data.drilldownInfo)
      }
      else
        reportPartContent.drilldownInfo = data.drilldownInfo;
    }

    if(!isNil(data.pointOptionsList)){
      if(this.type === constants.REPORT_PART_TYPE.Map) {
        const pointOptionsList = reportPartContent.pointOptionsList;
        pointOptionsList.clear();
        forEach(item => pointOptionsList.push(item), data.pointOptionsList)
      }
    }
  }

  /**
   * Get data by data key
   * @param {string} key
   * @returns {Object}
   */
  getDataValueByKey(key): any {
    return this[key];
  }

  /**
   * set Report Part Properties
   * @param {Object} reportPartProperties
   */
  @action
  setReportPartProperties(reportPartProperties: any): void {
    const reportPartContent = this.reportPartContent;

    if (!reportPartContent) {
      return;
    }

    if (reportPartContent.setReportPartProperties) {
      reportPartContent.setReportPartProperties(reportPartProperties);
      return;
    }

    this.reportPartContent.properties = {};
    $izenda.extend(true, this.reportPartContent.properties, reportPartProperties);
  }

  /**
   * Get Report Part Properties
   * @returns {Object}
   */
  getReportPartProperties(): any {
    if (!this.reportPartContent) {
      return null;
    }

    if (this.reportPartContent.getReportPartProperties) {
      return this.reportPartContent.getReportPartProperties();
    }

    return this.reportPartContent.properties;
  }

  /**
   * Set Config Field
   * @param {Object} field
   */
  @action
  setConfigField(field: any): void {
    this.configField = field;
    // Update selected html element on form.
    field &&
      this.reportPartContent.setSelectedFieldElement &&
      this.reportPartContent.setSelectedFieldElement(field.fieldNameAlias);

    // Update selected field to chart
    field && this.reportPartContent.setConfigField && this.reportPartContent.setConfigField(field);
  }

  /**
   * Reset Config Field
   */
  @action
  resetConfigField(): void {
    this.setConfigField(null);
  }

  /**
   * Mark all field visible
   */
  @action
  setAllFieldVisible(): void {
    this.fieldCollection.forEach(field => {
      field.properties.fieldItemVisible = true;
    });
    this.markDirty();
  }

  /**
   * Reload data based on data-refresh interval
   * @param {Object} dataRefreshInterval
   * @param {Function} fnGetData
   * @returns {undefined}
   */
  refreshData(dataRefreshInterval: any, fnGetData: () => mixed): any {
    dataRefreshInterval = dataRefreshInterval || {};

    const updateInterval = dataRefreshInterval.updateInterval;

    if (!dataRefreshInterval.enable || !dataRefreshInterval.updateInterval) {
      return this.clearRefreshData();
    }

    // refesh interval is changed
    if (this.refreshDataInterval !== updateInterval) {
      this.clearRefreshData();
    }

    if (!this.refreshDataProcess) {
      this.refreshDataInterval = updateInterval;
      this.refreshDataProcess = setInterval(() => {
        fnGetData(0);
      }, updateInterval * 1000);
    }
  }

  /**
   * Clear refresh data
   */
  clearRefreshData(): void {
    this.refreshDataProcess && clearInterval(this.refreshDataProcess);
    this.refreshDataProcess = null;
    this.refreshDataInterval = null;
  }

  /**
   * Calculate width height in px for container
   * @param {number} containerWidth
   * @param {number} containerHeigth
   * @returns {Object}
   */
  getContainerSize(containerWidth: number, containerHeigth: number): any {
    if (this.autoSize) {
      return {};
    }

    let width = BODY_CELL_WIDTH * this.width;
    let height = BODY_CELL_HEIGHT * this.height;

    if (containerWidth) {
      height = containerHeigth || (containerWidth * height) / width;
      width = containerWidth;
    }
    return this.autoSize
      ? {}
      : {
          height: height + 'px',
          width: width + 'px'
        };
  }

  /**
   * Get container info of report-part element
   * @param {Object} field
   */
  getContainerDetails(field: any): any {
    return untracked(() => {
      if (!field) {
        return null;
      }

      const fieldAlias = field.fieldName;
      let foundField;
      return find(container => {
        foundField =
          container.elements &&
          find(
            {
              fieldName: fieldAlias
            },
            container.elements
          );

        return !!foundField;
      }, this.containerCollection);
    });
  }

  /**
   * Start load data
   */
  @action
  startLoadData(): void {
    this.isLoadingData = true;
  }

  /**
   * Read embeded report
   * @param {Object} embededReportDef
   */
  readEmbededReportDef(embededReportDef: any): void {
    this.embedReportDef = embededReportDef;
  }

  /**
   * Update field name.
   * (For update name of calculated field)
   * @param {string} id
   * @param {string} originalName
   * @param {string} name
   * @param {Object} updatedCalField
   * @returns {boolean}
   */
  @action
  updateField(id: string, originalName: string, name: string, updatedCalField: any): boolean {
    let change = false;

    forEach(fieldDetail => {
      if (fieldDetail.field.fieldId === id) {
        // update field name.
        fieldDetail.field.fieldName = name;
        if (updatedCalField) {
          fieldDetail.field.hasAggregatedFunction = updatedCalField.hasAggregatedFunction;
          fieldDetail.field.hasSupportDefaultTotal = updatedCalField.hasSupportDefaultTotal;
          fieldDetail.field.isRunningField = updatedCalField.isRunningField;
        }
        change = true;
      }
    }, this.fieldCollection);

    this.reportPartContent.updateContentWithFieldName &&
      (change = this.reportPartContent.updateContentWithFieldName(originalName, name) || change);

    change && this.markDirty();
    return change;
  }

  /**
   * Check this report part has use calculated field
   * @param {string} id
   * @param {string} name
   * @returns {boolean}
   */
  hasCalculatedField(id, name): boolean {
    // Use in report part field
    let hasCalculatedField = !!find(fieldDetail => fieldDetail.field.fieldId === id, this.fieldCollection);

    // Use in grand total , sub total
    forEach(fieldDetail => {
      const subTotal = fieldDetail.properties.dataFormattings.subTotal;
      const grandTotal = fieldDetail.properties.dataFormattings.grandTotal;

      if (
        (subTotal &&
          subTotal.function === calculatedFieldConstant.FUNCTION_NAME.EXPRESSION &&
          subTotal.expression.indexOf('[' + name + ']') > -1) ||
        (grandTotal &&
          grandTotal.function === calculatedFieldConstant.FUNCTION_NAME.EXPRESSION &&
          grandTotal.expression.indexOf('[' + name + ']') > -1)
      ) {
        hasCalculatedField = true;
      }

      return !hasCalculatedField;
    }, this.fieldCollection);

    return hasCalculatedField;
  }

  /**
   * Cache element position and container after receive from api for sub report field mapping
   * @returns {Object}
   */
  buildElementInfo(): any {
    const getContainerName = this.reportPartContent.getContainerName || get('name');
    const processElement = container => {
      const containerName = getContainerName(container);

      return map(
        element => ({
          element: element,
          container: containerName,
          position: element.position
        }),
        container.elements
      );
    };

    return flatMap(processElement, this.containerCollection);
  }

  /**
   * Set cross filtering setting
   * @param {boolean} value
   */
  @action
  setCrossFiltering(value: boolean): void {
    this.reportPartContent.isCrossFiltering = value;
    this.markDirty();
  }

  /**
   * Do process after user change field name alias
   * + Update in Grand/Sub Total's Expression
   * + Update in Sub report mapping Fields
   * @param {string} oldAlias
   * @param {string} newAlias
   */
  @action
  processAfterChangeFieldNameAlias(oldAlias: string, newAlias: string): void {
    forEach(field => {
      field.properties.updateGrandSubTotal(oldAlias, newAlias);
    }, this.fieldCollection);

    let fieldSubreport = find(
      {
        fieldNameAlias: oldAlias
      },
      getOr([], 'properties.subReport.mappingFields', this.configField)
    );
    fieldSubreport && (fieldSubreport.fieldNameAlias = newAlias);

    this.reportPartContent.afterChangeFieldNameAlias && this.reportPartContent.afterChangeFieldNameAlias(oldAlias, newAlias);
  }

  /**
   * Get cross filtering filter
   * @returns {Array<any>}
   */
  getCrossFilteringFilter(): Array<any> {
    let filters = invoke('getCrossFilteringInfo', this.reportPartContent);
    if (!isEmpty(filters)) {
      return filters.map(filterItem => {
        return {
          key: get('key', filterItem),
          value: get('value', filterItem),
          fieldId: get('field.fieldId', filterItem),
          field: get('field', filterItem)
        };
      });
    }

    return [];
  }

  /**
   * Update State Review
   * @param {number} value
   */
  updateStateReview(value: number): void {
    this.stateReview = value;
  }

  /**
   * Get theme colors
   * @param {Object} props
   */
  getThemeColors(props: any): any {
    const theme = find(
      item => (isUndefined(props.colorTheme) ? item.isDefaultTheme : item.name === props.colorTheme),
      this.colorPalettes
    );
    return theme ? theme.colors : null;
  }

  /**
   * Clone report part detail
   * @return {Object}
   */
  clone = function() {
    const cloned = new ReportPartDetails();
    cloned.readData(this);
    cloned.headerTitle = this.headerTitle;

    const currentData = getOr({}, 'queryData[0]', this);
    cloned.setQueryData(currentData);
    return cloned;
  };

  /**
   * Update validate lookup filter error.
   * @param {Array<Object>} error
   */
  setLookupErrorMassage(error: Array<any>) {
    this.validateDetail = error;
  }

  /**
   * Get validate lookup filter error.
   * @param {Array<Object>} error
   */
  getLookupErrorMessage() {
    return head(get('messages', head(this.validateDetail)));
  }
}

export default ReportPartDetails;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartDetails.js